export type Section = 'completed' | 'ongoing' | 'planned';

export interface TimelineEntry {
  /** "2026-10-08" */
  date: string;
  phase: string;
  platform: string | null;
}

export interface ReportLine {
  section: Section;
  kind: 'epic' | 'task' | 'roadmap' | 'idea';
  key: string | null;
  project_key: string | null;
  project_name: string;
  feature: string;
  description: string;
  remark: string | null;
  links: Array<{ key: string; title: string; url: string }>;
  timeline: TimelineEntry[];
  planned_start: string | null;
}

export type Half = '1' | '2' | 'full';

export interface TechReport {
  period: { month: string; half: Half; title: string; start: string; end: string; as_at_label: string; is_current: boolean };
  highlights: Array<{ project_name: string; features: string[] }>;
  report: { completed: ReportLine[]; ongoing: ReportLine[]; planned: ReportLine[] };
  roadblocks: string;
  bug_fixes: number;
  hidden: Array<{ key: string; project_name: string; feature: string; section: Section }>;
  counts: { completed: number; ongoing: number; planned: number };
}

export interface KeptFile {
  id: number;
  period: string;
  half: Half;
  format: 'md' | 'docx';
  filename: string;
  bytes: number;
  sha256: string;
  kept_by: string | null;
  kept_at: string;
}

export const SECTION_TITLE: Record<Section, string> = { ongoing: 'Ongoing tasks', completed: 'Completed tasks', planned: 'Planned tasks' };
export const SECTION_ORDER: Section[] = ['ongoing', 'completed', 'planned'];

export const HALF_LABEL: Record<Half, string> = { '1': '1st to 15th', '2': '16th to the end', full: 'Whole month' };
export const HALVES: Half[] = ['1', '2', 'full'];

/** "08.10.2026": the report writes days as the team always has. */
export const reportDay = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

/** "08.10.2026 In design 14.10.2026 In progress", for a screen reader that reads a line at once. */
export const timelineText = (t: TimelineEntry[]): string => t.map((e) => `${reportDay(e.date)} ${e.phase}${e.platform ? ` (${e.platform})` : ''}`).join(', ');

export const bugFixesText = (n: number): string | null => (n > 0 ? `Also ${n} ${n === 1 ? 'bug fix' : 'bug fixes'} reached production, not listed one by one.` : null);
